import React, { SyntheticEvent, useEffect, useState } from "react";
import { Button, Card, Icon, Label, Popup } from "semantic-ui-react";
import LuxonDataTable from "../../components/LuxonDataTable";
import { formatDateTime } from "../../utils/datetimeUtils";
import InputSearch from "../../components/InputSearch";
import PageHeader from "../../components/PageHeader";
import { cancelAppointmentById, getProspectsAppointments, rescheduleProspectAppointmentById } from "../../services/prospects.service";
import { PROSPECTS_STATUSES } from "../../utils/constants";
import CancelAppointmentModal from "./modals/CancelAppointmentModal";
import { toast } from "react-toastify";
import ProspectCancelationCauseModal from "./modals/ProspectCancelationCauseModal";
import RescheduleAppointmentModal from "./modals/RescheduleAppointmentModal";
import RootTemplate from "../../components/RootTemplate";
import CreateNewProspectModal from "./modals/CreateNewProspect/CreateNewProspectModal";
import ProspectAppointment, { ProspectAppointmentStatus } from "../../types/entities/ProspectAppointment";
import UpdateProspectModal from "./modals/CreateNewProspect/UpdatedProspectModal";

const OrdersList = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);

    const [search, setSearch] = useState("");

    // State for Update Modal
    const [selectedProspectId, setSelectedProspectId] = useState<string>("");
    const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);

    const handleEdit = (id: string) => {
        setSelectedProspectId(id);
        setIsUpdateModalOpen(true);
    };

    const closeUpdateModal = () => {
        setIsUpdateModalOpen(false);
        setSelectedProspectId("");
    };

    const columns = [
        {
            name: "Fecha de visita",
            selector: (row: ProspectAppointment) => formatDateTime(row.appointment.dateTime),
        },
        {
            name: "Nombre del prospecto",
            selector: (row: ProspectAppointment) => row.prospect.person?.fullName || '',
        },
        {
            name: "Producto",
            selector: (row: ProspectAppointment) => row.prospect.product.name,
        },
        {
            name: "Asignado por",
            selector: (row: ProspectAppointment) => row.prospect.createdBy?.person?.fullName || '',
        },
        {
            name: "Estatus",
            cell: (row: ProspectAppointment) => (
                <>
                    {getStatusLabel(row.status)}
                    {row.type === "SHOWROOM" && (
                        <Popup
                            trigger={
                                <Icon circular name="home" />
                            }
                            content='Showroom Luxon'
                            position='top center'
                            size='small'
                            inverted
                        />
                    )}
                </>
            ),
        },
        {
            name: "Acciones",
            button: true,
            cell: (row: ProspectAppointment) => {
                if (row.status === "SCHEDULED") {
                    return (
                        <>
                            <CancelAppointmentModal prospectAppointmentId={row.id} onSubmit={handleCancelationAppointment} />
                            <Button color="yellow" icon size="mini" onClick={() => handleEdit(row.id)}>
                                <Icon name="pencil" />
                            </Button>
                        </>
                    );
                }
                if (row.status === "CANCELED") {
                    return (
                        <>
                            <ProspectCancelationCauseModal cancelationCause={row.appointment?.cancellationCause} />
                            <RescheduleAppointmentModal prospectAppointmentId={row.id} onSubmit={handleRescheduleAppointment} />
                        </>
                    );
                }
            },
        },
    ];

    const getStatusLabel = (status: ProspectAppointmentStatus) => (
        <Label color={PROSPECTS_STATUSES[status].color}>{PROSPECTS_STATUSES[status].name}</Label>
    );

    const handleCancelationAppointment = async (event: SyntheticEvent, prospectAppointmentId: string | undefined, cancellationCause: string) => {
        event.preventDefault();
        setLoading(true);
        try {

            if (prospectAppointmentId === undefined) {
                throw new Error("El id del prospecto no puede ser undefined.");
            }

            const response = await cancelAppointmentById(prospectAppointmentId, cancellationCause);
            if (response?.status === 200) {
                fetchData(1, search);
                toast.success("La cita fue cancelada con exito.");
            } else {
                throw new Error("Ha ocurrido un error al cancelar la cita para este prospecto.");
            }
        } catch (error: any) {
            toast.error(error.message);
            console.error("Error:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleRescheduleAppointment = async (event: SyntheticEvent, prospectAppointmentId: string | undefined, appointmentDate: Date | null) => {
        event.preventDefault();
        setLoading(true);
        try {

            if (prospectAppointmentId === undefined) {
                throw new Error("El id del prospecto no puede ser undefined.");
            }

            if (!appointmentDate) {
                throw new Error("Favor de seleccionar una fecha de la cita");
            }

            const response = await rescheduleProspectAppointmentById(prospectAppointmentId, appointmentDate);
            if (response?.status === 200) {
                fetchData(1, search);
                toast.success("La cita fue reagendada con exito.");
            } else {
                throw new Error("Ha ocurrido un error al mover el pedido a produccion.");
            }
        } catch (error: any) {
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchData = async (page: number, search?: string) => {
        setLoading(true);
        try {
            const response = await getProspectsAppointments(perPage, page - 1, "createdAt,desc", search, "SCHEDULED,CANCELED");
            setData(response?.data.content);
            setTotalRows(response?.data.totalElements);
        } catch (error) {
            console.error("Error al obtener la informacion:", error);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (page: number) => {
        fetchData(page);
    };

    const handlePerRowsChange = async (newPerPage: number, page: number) => {
        setLoading(true);
        try {
            const response = await getProspectsAppointments(newPerPage, page - 1, "createdAt,desc", search, "SCHEDULED,CANCELED");
            setData(response?.data.content);
            setTotalRows(response?.data.totalElements);
            setPerPage(newPerPage);
        } catch (error) {
            console.error("Error al obtener la informacion:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmitProspect = () => {
        fetchData(1);
    }

    const handleUpdateProspect = () => {
        // Just refresh the data, the modal will be closed by the UpdatedProspectModal calling onClose from props,
        // but we should technically wait for the data refresh.
        // The modal calls onProspectUpdated then onClose. 
        fetchData(1, search); // Refresh current view
    }

    useEffect(() => {
        fetchData(1);
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        fetchData(1, search);
        // eslint-disable-next-line
    }, [search]);

    return (
        <RootTemplate>
            <PageHeader
                title="Prospectos"
                subtitle="Clientes potenciales"
                actionComponents={
                    <CreateNewProspectModal onProspectCreated={handleSubmitProspect} />
                }
            />

            <InputSearch icon="search" placeholder="Búsqueda por nombre de prospecto..." setSearch={setSearch} />

            <Card fluid>
                <LuxonDataTable
                    columns={columns}
                    data={data}
                    loading={loading}
                    totalRows={totalRows}
                    handlePageChange={handlePageChange}
                    handlePerRowsChange={handlePerRowsChange}
                />
            </Card>

            <UpdateProspectModal
                isOpen={isUpdateModalOpen}
                onClose={closeUpdateModal}
                prospectAppointmentId={selectedProspectId}
                onProspectUpdated={handleUpdateProspect}
            />
        </RootTemplate>
    );
};

export default OrdersList;
